// src/components/CreativeManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import './AdminDashboard.css';
import './AdminDashboardModern.css';

const CreativeManager = () => {
  const navigate = useNavigate();
  const [creatives, setCreatives] = useState({ images: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    creative_format: 'image',
    description: ''
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [creativeToDelete, setCreativeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCreatives();
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchCreatives = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/get-creatives/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreatives({
          images: data.images || [],
          videos: data.videos || []
        });
      } else {
        console.error('Failed to fetch creatives');
      }
    } catch (error) {
      console.error('Error fetching creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreativeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const fileType = file.type;
    const isImage = fileType.startsWith('image/');
    const isVideo = fileType.startsWith('video/');

    if (!isImage && !isVideo) {
      alert('Please select an image or video file');
      setUploading(false);
      return;
    }

    const formData = new FormData();
    const endpoint = isImage ? 'upload-image' : 'upload-vid';
    const fieldName = isImage ? 'image' : 'video';
    const bucketName = isImage ? 'digital-ads-images' : 'digital-ads-video';

    formData.append(fieldName, file);
    formData.append('site_id', SITE_CONFIG.SITE_ID);
    formData.append('bucket_name', bucketName);

    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/${endpoint}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        fetchCreatives(); // Refresh the list
        event.target.value = ''; // Clear the input
      } else {
        alert(`Failed to upload ${isImage ? 'image' : 'video'}`);
      }
    } catch (error) {
      console.error('Error uploading creative:', error);
      alert(`Failed to upload ${isImage ? 'image' : 'video'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/ads/v1/request-creative', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'treas-api-key-2025'
        },
        body: JSON.stringify({
          ...requestForm,
          email: JSON.parse(localStorage.getItem('user'))?.email || '',
          site_id: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        setShowSuccess(true);
        setRequestForm({ creative_format: 'image', description: '' });
        setAcknowledged(false);
      } else {
        alert('Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCreative = async () => {
    if (!creativeToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/ads/v1/delete-creative/${creativeToDelete.file_id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        fetchCreatives(); // Refresh the list
        setShowDeleteModal(false);
        setCreativeToDelete(null);
      } else {
        alert('Failed to delete creative');
      }
    } catch (error) {
      console.error('Error deleting creative:', error);
      alert('Failed to delete creative');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-dashboard-modern">
      {/* Header */}
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">Creative Studio</h1>
            <p className="dashboard-subtitle">Manage and organize your advertising creatives and digital assets</p>
          </div>
          <div className="header-right">
            <label className="upload-btn-modern">
              <span className="btn-icon">📁</span>
              {uploading ? 'Uploading...' : 'Upload Asset'}
              <input 
                type="file" 
                accept="image/*,video/*" 
                onChange={handleCreativeUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
            <button 
              onClick={() => setShowRequestForm(true)} 
              className="request-btn-modern"
            >
              <span className="btn-icon">✨</span>
              Request Creative
            </button>
            <button onClick={() => navigate('/admin/digital-ads')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Digital Ads
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="admin-main-content">
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#FF6B35'}}>🖼️</div>
            <div className="stat-content">
              <div className="stat-number">{creatives.images.length}</div>
              <div className="stat-label">Images</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#9C27B0'}}>🎥</div>
            <div className="stat-content">
              <div className="stat-number">{creatives.videos.length}</div>
              <div className="stat-label">Videos</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon" style={{background: '#4CAF50'}}>📁</div>
            <div className="stat-content">
              <div className="stat-number">{creatives.images.length + creatives.videos.length}</div>
              <div className="stat-label">Total Assets</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading creative assets...</p>
          </div>
        ) : (
          <div className="creatives-sections">
            {/* Images Section */}
            <div className="creative-section-modern">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">🖼️</span>
                  Images ({creatives.images.length})
                </h2>
              </div>
              
              <div className="creative-grid-modern">
                {creatives.images.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🖼️</div>
                    <h3>No images yet</h3>
                    <p>Upload your first image to get started</p>
                  </div>
                ) : (
                  creatives.images.map((image) => (
                    <div key={image.file_id} className="creative-card-modern">
                      <div className="creative-media">
                        <img 
                          src={image.image_url} 
                          alt="Creative" 
                          className="creative-image-modern"
                        />
                        <div className="media-overlay">
                          <div className="media-type">IMAGE</div>
                        </div>
                        <button 
                          className="delete-creative-btn"
                          onClick={() => {
                            setCreativeToDelete(image);
                            setShowDeleteModal(true);
                          }}
                          title="Delete creative"
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="creative-details">
                        <div className="creative-meta">
                          <span className="creative-date">
                            {new Date(image.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                          <span className="creative-id">#{image.file_id.slice(-6)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Videos Section */}
            <div className="creative-section-modern">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">🎥</span>
                  Videos ({creatives.videos.length})
                </h2>
              </div>
              
              <div className="creative-grid-modern">
                {creatives.videos.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🎥</div>
                    <h3>No videos yet</h3>
                    <p>Upload your first video to get started</p>
                  </div>
                ) : (
                  creatives.videos.map((video) => (
                    <div key={video.file_id} className="creative-card-modern">
                      <div className="creative-media">
                        <video 
                          src={video.video_url} 
                          className="creative-video-modern"
                          controls
                          preload="metadata"
                        />
                        <div className="media-overlay">
                          <div className="media-type">VIDEO</div>
                        </div>
                        <button 
                          className="delete-creative-btn"
                          onClick={() => {
                            setCreativeToDelete(video);
                            setShowDeleteModal(true);
                          }}
                          title="Delete creative"
                        >
                          🗑️
                        </button>
                      </div>
                      <div className="creative-details">
                        <div className="creative-meta">
                          <span className="creative-date">
                            {new Date(video.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                          <span className="creative-id">#{video.file_id.slice(-6)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Success Modal */}
      {showSuccess && (
        <div className="modal-overlay-modern" onClick={() => { setShowSuccess(false); setShowRequestForm(false); }}>
          <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Request Submitted!</h3>
              <button 
                className="modal-close"
                onClick={() => { setShowSuccess(false); setShowRequestForm(false); }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="success-icon-modern">✓</div>
              <p className="success-message">
                Your custom creative request has been submitted successfully!
              </p>
              <p className="success-note">
                We'll begin working on your creative and will have it ready within 48-72 hours. 
                You'll receive an email notification once it's completed.
              </p>
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => { setShowSuccess(false); setShowRequestForm(false); }}
                className="btn-primary"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Creative</h3>
              <button 
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this creative?</p>
              <div className="delete-preview">
                {creativeToDelete && (
                  <div className="preview-item">
                    {creativeToDelete.image_url ? (
                      <img src={creativeToDelete.image_url} alt="Creative to delete" style={{maxWidth: '200px', maxHeight: '150px', objectFit: 'cover'}} />
                    ) : (
                      <video src={creativeToDelete.video_url} style={{maxWidth: '200px', maxHeight: '150px'}} />
                    )}
                    <p>ID: #{creativeToDelete.file_id?.slice(-6)}</p>
                  </div>
                )}
              </div>
              <p><strong>This action cannot be undone.</strong></p>
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteCreative}
                className="btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Creative'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Creative Modal */}
      {showRequestForm && !showSuccess && (
        <div className="modal-overlay-modern" onClick={() => setShowRequestForm(false)}>
          <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Request Custom Creative</h3>
              <button 
                className="modal-close"
                onClick={() => setShowRequestForm(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleRequestSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Creative Type</label>
                  <select
                    className="form-select-modern"
                    value={requestForm.creative_format}
                    onChange={(e) => setRequestForm({...requestForm, creative_format: e.target.value})}
                    required
                  >
                    <option value="image">Image Creative</option>
                    <option value="video">Video Creative</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea-modern"
                    placeholder="Describe your creative requirements in detail..."
                    value={requestForm.description}
                    onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                    rows="4"
                    required
                  />
                </div>
                
                <div className="acknowledgment-section-modern">
                  <label className="checkbox-label-modern">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      required
                    />
                    <div className="checkbox-content">
                      <div className="checkbox-title">Service Agreement</div>
                      <div className="checkbox-text">
                        I acknowledge that requesting a custom creative includes up to 2 review/edit cycles, 
                        takes 48-72 hours to complete, and costs $50 which will be added to my next monthly bill.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={() => setShowRequestForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting || !acknowledged}
                  className="btn-primary"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeManager;